import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/shared/lib/cn';

const buttonVariants = cva(
  'flex items-center justify-center gap-1 whitespace-nowrap transition-all disabled:cursor-not-allowed disabled:opacity-50 shrink-0 cursor-pointer outline-none ' +
    "[&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 [&_svg]:shrink-0 " +
    'focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] ' +
    'aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive',
  {
    variants: {
      variant: {
        primary: 'bg-blue-600 text-white hover:bg-blue-700',
        secondary:
          'bg-white border border-lt-gray-300 px-6 py-2.5 hover:border-lt-gray-300 transition-colors text-foreground text-sm font-semi-bold',
        blackButton:
          'bg-black text-white px-6 py-2.5 font-normal hover:bg-gray-800 transition-colors text-sm',
        whiteButton:
          'bg-white border border-lt-gray-300 px-6 py-2.5 hover:border-lt-gray-300 transition-colors text-foreground text-sm font-semi-bold',
        login:
          'bg-black hover:bg-gray-800 text-white shadow-lg shadow-black/30 text-sm font-medium inline-flex xl:h-12 2xl:h-14 ',
        verify:
          'bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-600/30',
        // Cancel / outline button
        outlineGray:
          'border border-slate-200 bg-background text-foreground hover:bg-slate-50 hover:text-accent-foreground ' +
          'dark:bg-input/30 dark:border-input dark:hover:bg-input/50',
        // Send reset link (same blue style, explicit semantic name)
        primaryBlue:
          'text-sm  transition-all py-2 px-5 h-10 bg-[#0052CC] hover:bg-[#0747A6] text-white disabled:cursor-not-allowed disabled:opacity-50 outline-none focus-visible:ring-2 focus-visible:ring-[#0747A6]',
        sidebarExpandButton:
          'text-white hover:text-accent-foreground dark:hover:bg-accent/50 hover:bg-lt-gray-800 transition-all duration-200 hover:scale-110',
        sidebarLogoutButton:
          'group w-full justify-start text-base gap-3 px-3 py-3 hover:bg-gray-800 hover:text-white hover:shadow-lg hover:shadow-gray-900/50 transition-all duration-200 text-gray-300 relative overflow-hidden ',
        languageSelectTrigger:
          'justify-between border px-3 py-2 text-sm whitespace-nowrap gap-2 ' +
          'transition-[color,box-shadow] outline-none focus-visible:ring-[3px] ' +
          'disabled:cursor-not-allowed disabled:opacity-50 ' +
          'sm:w-[160px] w-[140px] h-9 border-slate-200 bg-white hover:bg-slate-50 ' +
          "[&_svg:not([class*='text-'])]:text-muted-foreground",
        // New color variants
        success: 'bg-green-600 hover:bg-green-700 text-white',
        danger:
          'cursor-pointer px-2 text-sm font-medium transition break-all bg-red-600 text-white hover:bg-red-700',
        warning: 'bg-yellow-600 hover:bg-yellow-700 text-white',
        secondaryBtn:
          'text-blue-600 hover:bg-blue-50 hover:text-blue-700 dark:hover:bg-accent/50',
        outline:
          'border-2 border-gray-300 bg-transparent hover:bg-gray-50 text-gray-700',
        yellowButton: 'bg-yellow-600 hover:bg-yellow-700 text-white',
        primaryText:
          'text-sm font-medium text-blue-600 hover:bg-blue-50 hover:text-blue-700 px-4 py-2 h-11',
        bluePill:
          'h-12 px-12 py-2 bg-[rgb(0,153,255)] hover:bg-blue-700 text-white shadow-lg shadow-blue-600/30 text-sm font-semibold',
        greenBtn:
          'bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 px-4 py-2 text-sm font-medium text-white shadow-lg',
      },
      size: {
        default: 'px-4 py-2',
        full: 'w-full h-14 px-4 py-2',
        fullSm: 'w-full h-12 px-4 py-2',
        flex: 'flex-1 h-14 px-4 py-2',
        xl: 'w-full h-14 px-4 py-2',
        icon9: 'h-9 w-9',
        sm: 'px-3 py-1.5 text-sm',
        md: 'px-4 py-2 text-base',
        lg: 'px-6 py-3 text-lg',
      },
      rounded: {
        none: 'rounded-none',
        sm: 'rounded-sm',
        md: 'rounded-md',
        lg: 'rounded-lg',
        xl: 'rounded-xl',
        full: 'rounded-full',
      },
      align: {
        left: 'justify-start',
        center: 'justify-center',
        right: 'justify-end',
      },
    },
    defaultVariants: {
      variant: 'login',
      rounded: 'lg',
      align: 'center',
    },
  }
);

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  /** Icon to display at the start of the button */
  icon?: React.ReactNode;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      rounded,
      align,
      icon,
      asChild = false,
      children,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : 'button';

    return (
      <Comp
        ref={ref}
        className={cn(
          buttonVariants({ variant, size, rounded, align }),
          className
        )}
        {...props}
      >
        {icon && <span className="inline-flex items-center">{icon}</span>}
        {children}
      </Comp>
    );
  }
);

Button.displayName = 'Button';

export { Button };
